import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import {
  LOG_LEVEL,
  Purchases,
  PurchasesPackage,
  PurchasesOfferings,
} from '@revenuecat/purchases-capacitor';
import { environment } from '../../environments/environment';
import type { PlanType } from './checkout.service';
import { IAP_PACKAGE_IDS, IAP_PRODUCT_IDS } from './iap-products';

export interface IapPlanPrice {
  price: string;
  priceDetail?: string;
}

@Injectable({ providedIn: 'root' })
export class IapService {
  private readonly platformId = inject(PLATFORM_ID);
  private configured = false;
  private initPromise: Promise<void> | null = null;
  private offeringsCache: PurchasesOfferings | null = null;

  readonly nativeCheckoutAvailable = signal(false);
  readonly restoring = signal(false);

  isNativePlatform(): boolean {
    return isPlatformBrowser(this.platformId) && Capacitor.isNativePlatform();
  }

  async initialize(): Promise<void> {
    if (!this.isNativePlatform()) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.configure();
    return this.initPromise;
  }

  private async configure(): Promise<void> {
    const apiKey = this.resolveApiKey();
    if (!apiKey) {
      console.warn('RevenueCat API key not configured for this platform');
      return;
    }

    try {
      await Purchases.setLogLevel({ level: environment.production ? LOG_LEVEL.WARN : LOG_LEVEL.DEBUG });
      await Purchases.configure({ apiKey });
      this.configured = true;
      this.nativeCheckoutAvailable.set(true);
    } catch (err) {
      console.error('RevenueCat configure failed', err);
      this.nativeCheckoutAvailable.set(false);
      this.initPromise = null;
    }
  }

  async syncUser(userId: string | null): Promise<void> {
    // The auth effect can fire before configure() resolves; waiting here keeps the
    // RevenueCat app user id from staying on $RCAnonymousID.
    await this.initialize();
    if (!this.configured) {
      return;
    }

    try {
      if (userId) {
        await Purchases.logIn({ appUserID: userId });
      } else {
        await Purchases.logOut();
      }
    } catch (err) {
      console.warn('RevenueCat user sync failed', err);
    }
  }

  async loadPlanPrices(): Promise<Partial<Record<PlanType, IapPlanPrice>>> {
    await this.initialize();
    if (!this.configured) {
      return {};
    }

    try {
      const offerings = await this.getOfferings();
      const current = offerings.current;
      if (!current) {
        return {};
      }

      const result: Partial<Record<PlanType, IapPlanPrice>> = {};
      for (const plan of ['jahrespass', 'herbstpass', 'einzelwanderung'] as const) {
        const pkg = this.findPackage(current.availablePackages, plan);
        const product = pkg?.product;
        if (!product?.priceString) {
          continue;
        }

        result[plan] = {
          price: product.priceString,
          priceDetail: this.buildPriceDetail(plan, product.price, product.currencyCode),
        };
      }
      return result;
    } catch (err) {
      console.warn('RevenueCat offerings unavailable', err);
      return {};
    }
  }

  /**
   * @param expectedAppUserId Supabase uuid the webhook will grant to; re-logs in if RevenueCat
   *   still reports a different (anonymous SDK) id.
   */
  async purchase(plan: PlanType, expectedAppUserId?: string): Promise<void> {
    await this.initialize();
    if (!this.configured) {
      throw new Error('In-App-Käufe sind nicht verfügbar');
    }

    if (expectedAppUserId) {
      const { appUserID } = await Purchases.getAppUserID();
      if (appUserID !== expectedAppUserId) {
        await Purchases.logIn({ appUserID: expectedAppUserId });
      }
    }

    const offerings = await this.getOfferings();
    const current = offerings.current;
    if (!current) {
      throw new Error('Keine Produkte verfügbar');
    }

    const pkg = this.findPackage(current.availablePackages, plan);
    if (!pkg) {
      throw new Error('Produkt nicht gefunden');
    }

    await Purchases.purchasePackage({ aPackage: pkg });
  }

  async restorePurchases(): Promise<boolean> {
    await this.initialize();
    if (!this.configured) {
      return false;
    }

    this.restoring.set(true);
    try {
      await Purchases.restorePurchases();
      return true;
    } finally {
      this.restoring.set(false);
    }
  }

  private async getOfferings(): Promise<PurchasesOfferings> {
    if (this.offeringsCache) {
      return this.offeringsCache;
    }

    const offerings = await Purchases.getOfferings();
    this.offeringsCache = offerings;
    return offerings;
  }

  private findPackage(packages: PurchasesPackage[], plan: PlanType): PurchasesPackage | undefined {
    const packageId = IAP_PACKAGE_IDS[plan];
    const productId = IAP_PRODUCT_IDS[plan];

    return (
      packages.find((p) => p.identifier === packageId) ??
      packages.find((p) => p.product.identifier === productId) ??
      packages.find((p) => p.product.identifier.startsWith(productId))
    );
  }

  private resolveApiKey(): string {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return environment.revenueCatIosApiKey ?? '';
    }
    if (platform === 'android') {
      return environment.revenueCatAndroidApiKey ?? '';
    }
    return '';
  }

  private buildPriceDetail(
    plan: PlanType,
    price: number | null | undefined,
    currencyCode: string | null | undefined
  ): string | undefined {
    if (price == null || Number.isNaN(price)) {
      return undefined;
    }

    if (plan === 'einzelwanderung') {
      return 'einmalig · dauerhaft';
    }

    const months = plan === 'jahrespass' ? 12 : 3;
    const perMonth = price / months;
    return `${currencyCode || 'CHF'} ${perMonth.toFixed(2)} pro Monat`;
  }
}
